/**
 * CustomerHeaderAvatar — the LINE profile picture shown left of the title (like
 * the product image on item pages), with two corner badges that answer "where
 * did this customer come from, and are we already talking to them?":
 *
 *   • Channel badge — pinned to the avatar's top-right corner, showing the
 *     most-recent marketplace they ordered through (Shopee / Lazada / TikTok),
 *     else LINE if they logged in via LINE. Storefront/web customers get none.
 *   • Chat badge — a LINE-green "chat dots" bubble (ChatBubbleBadge), offset
 *     further right so it floats clear of the photo, shown when a Chatwoot
 *     conversation exists.
 *
 * `CustomerLastMessage` renders a one-line preview of the customer's latest LINE
 * message under the name (messaging-app style), truncated with an ellipsis, led
 * by the SAME ChatBubbleBadge. Both share `useCustomerChatSummary`, so they
 * dedup into one request.
 */

import type { ReactNode } from "react";
import { useChatPanelStore } from "@/lib/store/chatPanelStore";
import {
  useCustomerAvatar,
  useCustomerOrders,
  useCustomerChatSummary,
} from "@/features/customers/api/customerHooks";
import { HeaderMediaThumbnail } from "@/features/inventory/item-detail/components/HeaderMediaThumbnail";
import { LineIcon, ShopeeIcon, LazadaIcon, TikTokIcon } from "@/components/icons/BrandIcons";
import { parseLoginIdentities, type PortalUserRow } from "./AccountIdentityCard";
import type { CustomerChatMessage } from "@/features/customers/api/customerConversation";
import type { SalesOrder } from "@/lib/erp/types/order";

const LINE_GREEN = "#06C755";

type Channel = "LINE" | "Shopee" | "Lazada" | "TikTok";

/** Brand glyph per channel — colored mark meant to sit on a white tile. */
const CHANNEL_GLYPH: Record<Channel, (className: string) => ReactNode> = {
  LINE: (c) => <LineIcon className={c} />,
  Shopee: (c) => <ShopeeIcon className={c} />,
  Lazada: (c) => <LazadaIcon className={c} />,
  TikTok: (c) => <TikTokIcon className={c} />,
};

/** Resolve the badge channel: most-recent marketplace order wins, else LINE login. */
function resolveChannel(orders: SalesOrder[], lineUserId?: string): Channel | null {
  const byRecent = orders
    .slice()
    .sort((a, b) =>
      String((b as Record<string, any>).transaction_date || "").localeCompare(
        String((a as Record<string, any>).transaction_date || ""),
      ),
    );
  for (const o of byRecent) {
    const c = String((o as Record<string, any>).source_channel || "");
    if (/shopee/i.test(c)) return "Shopee";
    if (/lazada/i.test(c)) return "Lazada";
    if (/tiktok/i.test(c)) return "TikTok";
  }
  if (lineUserId) return "LINE";
  return null;
}

/** Pull the LINE user id from the Customer's portal_users (no extra fetch). */
function lineIdOf(formData: Record<string, any>): string | undefined {
  return parseLoginIdentities(formData.portal_users as PortalUserRow[] | undefined).find(
    (l) => l.method === "line",
  )?.lineUserId;
}

/** Latest message the customer sent (incoming), else the latest message overall. */
function latestCustomerMessage(messages?: CustomerChatMessage[]): string | null {
  const withText = (messages ?? []).filter((m) => m.content && m.content.trim());
  if (!withText.length) return null;
  const incoming = withText.filter((m) => m.incoming);
  const pool = incoming.length ? incoming : withText;
  // messages come newest-last; sort by timestamp to be safe, take the latest.
  const latest = pool
    .slice()
    .sort((a, b) => String(a.at || "").localeCompare(String(b.at || "")))
    .at(-1);
  return latest?.content.trim() ?? null;
}

/** Shared white squircle tile with a soft shadow + white halo ring. */
const BADGE_TILE =
  "flex items-center justify-center rounded-[10px] bg-white shadow-[0_2px_6px_rgba(0,0,0,0.22)] ring-2 ring-white dark:bg-neutral-900 dark:ring-neutral-900";

export function CustomerHeaderAvatar({
  customer,
  formData,
}: {
  customer: string;
  formData: Record<string, any>;
}) {
  const { data: avatarUrl } = useCustomerAvatar(customer);
  const { data: orders } = useCustomerOrders(customer);
  const lineUserId = lineIdOf(formData);
  const channel = resolveChannel(orders ?? [], lineUserId);

  const { data: chat } = useCustomerChatSummary(lineUserId);
  const hasChat = !!(chat && (chat.conversation_id != null || (chat.messages?.length ?? 0) > 0));
  const message = latestCustomerMessage(chat?.messages);

  // Same action as the timeline chat + "Open in Inbox": pop open the floating
  // Chatwoot panel on this customer's conversation.
  const openChatPanel = useChatPanelStore((s) => s.openConversation);
  const openChat = () => {
    if (chat?.account_id != null && chat?.conversation_id != null) {
      openChatPanel(chat.account_id, chat.conversation_id);
    }
  };

  if (!avatarUrl) return null;

  return (
    <div className="relative shrink-0">
      {/* Fixed avatar size — override HeaderMediaThumbnail's product-page
          default (w-full h-full on mobile), which has no dimensions here. */}
      <HeaderMediaThumbnail
        image={avatarUrl}
        video={null}
        alt={customer}
        className="h-16 w-16 md:h-28 md:w-28 rounded-xl"
      />

      {/* Channel badge — pinned to the avatar's top-right corner. */}
      {channel && (
        <span className={`absolute -top-2 -right-2 h-7 w-7 ${BADGE_TILE}`} title={`Came from ${channel}`}>
          {CHANNEL_GLYPH[channel]("h-[18px] w-[18px] object-contain")}
        </span>
      )}

      {/* Chat bubble — ONE LINE-green speech bubble that contains the latest
          message (dotted chat glyph + text). A little tail points back toward
          the avatar so it reads as a chat bubble, not a pill. Floats just right
          of the avatar's top corner; icon-only when there's no message. */}
      {hasChat && (
        <button
          type="button"
          onClick={openChat}
          aria-label="Open LINE conversation"
          className={`absolute -top-2 left-full ml-3 hidden md:flex max-w-[380px] origin-bottom-left animate-bubble-pop-in items-center rounded-2xl rounded-bl-md text-left shadow-[0_2px_6px_rgba(0,0,0,0.22)] transition-[filter] hover:brightness-105 active:brightness-95 [animation-delay:320ms] ${
            message ? "gap-1 py-1.5 pl-2 pr-3" : "h-7 w-7 justify-center"
          }`}
          style={{ backgroundColor: LINE_GREEN }}
          title={message ?? "LINE conversation"}
        >
          {/* Tail — a small triangle at the bottom-left pointing at the avatar. */}
          <span
            aria-hidden
            className="absolute -left-[5px] bottom-1 h-0 w-0 border-y-[5px] border-r-[7px] border-y-transparent"
            style={{ borderRightColor: LINE_GREEN }}
          />
          <svg viewBox="0 0 24 24" fill="white" className="h-4 w-4 shrink-0">
            <path d="M12 3C6.48 3 2 6.58 2 11c0 2.05.97 3.92 2.56 5.34-.12.9-.5 2.16-1.4 3.14-.24.26-.06.7.29.66 1.64-.2 3.03-.86 3.96-1.42A11.6 11.6 0 0 0 12 19c5.52 0 10-3.58 10-8s-4.48-8-10-8Z" />
            <circle cx="8.4" cy="11" r="1.15" fill={LINE_GREEN} />
            <circle cx="12" cy="11" r="1.15" fill={LINE_GREEN} />
            <circle cx="15.6" cy="11" r="1.15" fill={LINE_GREEN} />
          </svg>
          {message && (
            <span className="truncate text-[13px] font-medium leading-tight text-white">{message}</span>
          )}
        </button>
      )}
    </div>
  );
}
